import { Button } from "@/components/ui/Button";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/Drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Separator } from "@/components/ui/Separator";
import { useSearch } from "@/contexts/SearchContext";
import { PropertySorting } from "@/enums/property.enum";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { RefObject, useRef } from "react";
import { useOnClickOutside, useToggle, useWindowSize } from "usehooks-ts";

export const SortingMenu = () => {
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const [open, toggleOpen] = useToggle();
  const t = useTranslations("listing.sorting");
  const { width } = useWindowSize();
  const { sort, updateSort } = useSearch();

  useOnClickOutside(drawerContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      toggleOpen();
    }
  });

  if (width < 768) {
    return (
      <Drawer open={open} key={"sorting-menu-drawer"}>
        <DrawerTrigger asChild className="h-full text-base">
          <Button variant={sort ? "active" : "default"} onClick={toggleOpen}>
            {t(`options.${sort}`)}
            <ChevronDown className="size-4.5" />
          </Button>
        </DrawerTrigger>

        <DrawerContent
          className="w-full flex flex-col gap-4 p-2"
          ref={drawerContentRef}
        >
          <DrawerTitle>{t("title")}</DrawerTitle>
          <div className="flex gap-2 flex-wrap">
            {Object.values(PropertySorting).map((sort) => (
              <Button
                variant={sort == sort ? "secondary" : "tertiary"}
                key={sort}
                onClick={() => updateSort(sort)}
                className="rounded-full capitalize"
              >
                {t(`options.${sort}`)}
              </Button>
            ))}
          </div>
          <Separator />
          <div className="space-x-2 flex items-end w-full">
            <Button
              variant={"tertiary"}
              onClick={() => updateSort(PropertySorting.FEATURED)}
              className="w-full"
            >
              {t("cta.reset")}
            </Button>
            <Button
              variant={"secondary"}
              onClick={toggleOpen}
              className="w-full"
            >
              {t("cta.done")}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Select
      defaultValue={sort}
      value={sort}
      onValueChange={(value) => updateSort(value as PropertySorting)}
      size="sm"
    >
      <SelectTrigger
        className="w-[180px] !truncate"
        variant={sort ? "active" : "default"}
      >
        <SelectValue placeholder="Sort" className="w-1/2" defaultValue={sort} />
      </SelectTrigger>
      <SelectContent>
        {Object.values(PropertySorting).map((sort) => (
          <SelectItem value={sort} key={sort}>
            {t(`options.${sort}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
